import React from 'react';

import {
  fetchMfaPolicy,
  getDefaultMfaPolicy,
  saveMfaPolicy,
  type MfaMethod,
  type MfaPolicy,
  type RoleMfaPolicy,
} from '@/services/mfaSettingsApi';

const ROLE_ORDER: Array<keyof MfaPolicy> = ['platform_admin', 'operator', 'tenant_user'];
const ROLE_LABELS: Record<keyof MfaPolicy, string> = {
  platform_admin: 'Platform Admin',
  operator: 'Operator',
  tenant_user: 'Tenant User',
};
const TRUSTED_DEVICE_DAY_OPTIONS = [7, 14, 30, 60] as const;

const METHOD_OPTIONS: Array<{ value: MfaMethod; label: string; disabled?: boolean }> = [
  { value: 'email_otp', label: 'Email OTP' },
  { value: 'totp', label: 'TOTP (coming soon)', disabled: true },
  { value: 'webauthn', label: 'WebAuthn (coming soon)', disabled: true },
];

const cardStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 16,
  marginBottom: 16,
  background: '#ffffff',
};

const normalizeTrustedDeviceDays = (candidate: number) =>
  TRUSTED_DEVICE_DAY_OPTIONS.includes(candidate as (typeof TRUSTED_DEVICE_DAY_OPTIONS)[number])
    ? candidate
    : 30;

const mapSliderValueToTrustedDays = (sliderValue: number) => {
  const boundedIndex = Math.min(
    TRUSTED_DEVICE_DAY_OPTIONS.length - 1,
    Math.max(0, Math.round(sliderValue))
  );
  return TRUSTED_DEVICE_DAY_OPTIONS[boundedIndex];
};

const getTrustedDaysSliderValue = (trustedDays: number) => {
  const normalized = normalizeTrustedDeviceDays(trustedDays);
  const index = TRUSTED_DEVICE_DAY_OPTIONS.indexOf(normalized as (typeof TRUSTED_DEVICE_DAY_OPTIONS)[number]);
  return index < 0 ? 2 : index;
};

export default function MfaSettingsPage() {
  const [policy, setPolicy] = React.useState<MfaPolicy>(getDefaultMfaPolicy());
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const [savedAt, setSavedAt] = React.useState<string>('');

  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsLoading(true);
      setError('');
      try {
        const loadedPolicy = await fetchMfaPolicy();
        if (mounted) {
          setPolicy(loadedPolicy);
        }
      } catch (loadError) {
        if (mounted) {
          setError((loadError as Error).message || 'Failed to load MFA policy');
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const updateRolePolicy = (role: keyof MfaPolicy, updater: (current: RoleMfaPolicy) => RoleMfaPolicy) => {
    setPolicy(prev => ({
      ...prev,
      [role]: updater(prev[role]),
    }));
    setSavedAt('');
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSavedAt('');
    try {
      const savedPolicy = await saveMfaPolicy(policy);
      setPolicy(savedPolicy);
      setSavedAt(new Date().toLocaleString());
    } catch (saveError) {
      setError((saveError as Error).message || 'Failed to save MFA policy');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <main style={{ fontFamily: 'Inter, sans-serif', padding: 24, maxWidth: 960 }}>
      <h1 style={{ marginBottom: 8 }}>MFA Settings</h1>
      <p style={{ color: '#4b5563', marginBottom: 16 }}>
        MFA framework is deployed and currently disabled by default. Enable by role when email infrastructure is ready.
      </p>

      {error ? (
        <div style={{ ...cardStyle, borderColor: '#fecaca', background: '#fef2f2', color: '#b91c1c' }}>
          {error}
        </div>
      ) : null}

      {savedAt ? (
        <div style={{ ...cardStyle, borderColor: '#bbf7d0', background: '#f0fdf4', color: '#166534' }}>
          Saved successfully at {savedAt}
        </div>
      ) : null}

      {isLoading ? (
        <div style={cardStyle}>Loading MFA policy...</div>
      ) : (
        <>
          {ROLE_ORDER.map(role => {
            const rolePolicy = policy[role];
            return (
              <section key={role} style={cardStyle}>
                <h2 style={{ marginTop: 0 }}>{ROLE_LABELS[role]}</h2>
                <div style={{ display: 'grid', gap: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={rolePolicy.enabled}
                      onChange={event =>
                        updateRolePolicy(role, current => ({
                          ...current,
                          enabled: event.target.checked,
                        }))
                      }
                    />
                    Enable MFA for {ROLE_LABELS[role]}
                  </label>

                  <label style={{ display: 'grid', gap: 6 }}>
                    <span>Method</span>
                    <select
                      value={rolePolicy.method}
                      onChange={event =>
                        updateRolePolicy(role, current => ({
                          ...current,
                          method: event.target.value as MfaMethod,
                        }))
                      }
                    >
                      {METHOD_OPTIONS.map(option => (
                        <option key={option.value} value={option.value} disabled={Boolean(option.disabled)}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label style={{ display: 'grid', gap: 6 }}>
                    <span>
                      Trusted device duration: <strong>{normalizeTrustedDeviceDays(rolePolicy.trusted_device_days)} days</strong>
                    </span>
                    <input
                      type="range"
                      min={0}
                      max={TRUSTED_DEVICE_DAY_OPTIONS.length - 1}
                      step={1}
                      value={getTrustedDaysSliderValue(rolePolicy.trusted_device_days)}
                      onChange={event => {
                        const sliderValue = Number(event.target.value);
                        updateRolePolicy(role, current => ({
                          ...current,
                          trusted_device_days: mapSliderValueToTrustedDays(sliderValue),
                        }));
                      }}
                    />
                    <small style={{ color: '#6b7280' }}>{TRUSTED_DEVICE_DAY_OPTIONS.join(' / ')} days</small>
                  </label>
                </div>
              </section>
            );
          })}

          <div style={{ display: 'flex', gap: 12 }}>
            <button type="button" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save MFA policy'}
            </button>
          </div>
        </>
      )}
    </main>
  );
}
